/**
 * Shared profanity list + helpers used by BOTH the server (mask chat, reject
 * profane names/shouts) and the client (warn the player as they type). Keeping
 * one source of truth avoids the client and server disagreeing on what's
 * allowed. Not exhaustive — it satisfies App Store Guideline 1.2's "filter
 * objectionable material" requirement across every language the app ships in
 * (EN, VI, ZH, JA, KO, TH).
 */

// Keep the list compact and lowercase. Matching is case-insensitive and uses
// substring matching (CJK / Thai have no word spaces), so multi-char phrases are
// preferred for those scripts to avoid masking innocuous single characters.
const BLOCKLIST: string[] = [
  // English (common obscenities / slurs)
  'fuck', 'fucker', 'fucking', 'shit', 'bitch', 'cunt', 'asshole', 'bastard',
  'dick', 'pussy', 'whore', 'slut', 'nigger', 'nigga', 'faggot', 'retard',
  'rape', 'rapist',
  // Vietnamese (common obscenities)
  'địt', 'đụ', 'lồn', 'cặc', 'buồi', 'đĩ', 'đm', 'đmm', 'vcl', 'vl', 'cứt',
  'đéo', 'dái', 'đĩ điếm', 'con đĩ', 'thằng chó', 'óc chó',
  // Chinese — prefer multi-char phrases so legit chars (操/干/草) aren't hit.
  '他妈的', '妈的', '妈逼', '傻逼', '傻屄', '草泥马', '卧槽', '我操', '操你',
  '肏', '干你娘', '狗娘养的', '王八蛋', '婊子', '贱人', '鸡巴', '屌', '死全家',
  // Japanese
  '死ね', 'くそ', 'クソ', 'ちんこ', 'ちんぽ', 'まんこ', 'やりまん',
  'ぶっ殺す', 'きちがい', '気違い',
  // Korean
  '씨발', '시발', '씨팔', '개새끼', '새끼', '좆같', '좆까', '병신', '지랄',
  '닥쳐', '보지', '자지', '창녀', '개년', '미친놈', '썅',
  // Thai
  'เหี้ย', 'สัส', 'ไอ้สัส', 'ควย', 'หี', 'เย็ด', 'แม่ง', 'อีดอก', 'กระหรี่',
  'ชิบหาย', 'ส้นตีน', 'เงี่ยน',
];

function buildPattern(): RegExp {
  return new RegExp(
    `(${BLOCKLIST.map(escapeRegExp).sort((a, b) => b.length - a.length).join('|')})`,
    'giu',
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const PATTERN = buildPattern();

/** Replace blocklisted words with asterisks of the same length (for chat). */
export function maskProfanity(text: string): string {
  return text.replace(PATTERN, (m) => '*'.repeat(m.length));
}

/** True if the text contains any blocklisted word (for names / shouts, which
 *  are rejected rather than masked). Stateless — uses a fresh regex so the
 *  shared global-flag PATTERN's lastIndex can't leak between calls. */
export function containsProfanity(text: string): boolean {
  return buildPattern().test(text);
}
